import React, { useState, useEffect } from "react";
import { FaCog, FaPlus, FaEdit, FaToggleOn, FaToggleOff } from "react-icons/fa";
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Button,
  TextField,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Badge,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
} from "@mui/material";
import {
  createWorkType,
  getAllWorkTypes,
  toggleWorkTypeStatus,
  updateWorkType,
} from "../../Services/workTypeService";
import {
  createCompensationType,
  getAllCompensationTypes,
  toggleCompensationTypeStatus,
  updateCompensationType,
} from "../../Services/compensationService";

export default function AdminManagementPage() {
  const [workTypes, setWorkTypes] = useState([]);
  const [compensationTypes, setCompensationTypes] = useState([]);
  const [editingWorkType, setEditingWorkType] = useState(null);
  const [editingCompensationType, setEditingCompensationType] = useState(null);
  const [newWorkTypeName, setNewWorkTypeName] = useState("");
  const [newCompensationTypeName, setNewCompensationTypeName] = useState("");
  const [tabValue, setTabValue] = useState("workTypes");

  const fetchWorkTypes = async () => {
    const responseData = await getAllWorkTypes();
    setWorkTypes(responseData?.data);
  };

  const fetchCompensationTypes = async () => {
    const responseData = await getAllCompensationTypes();
    setCompensationTypes(responseData?.data);
  };

  useEffect(() => {
    fetchWorkTypes();
    fetchCompensationTypes();
  }, []);

  const addWorkType = () => {
    if (!newWorkTypeName.trim()) return;

    const newWorkType = {
      name: newWorkTypeName,
      active: true,
    };

    const createNewWorkType = async () => {
      const responseData = await createWorkType(newWorkType);
      fetchWorkTypes();
    };
    createNewWorkType();

    setNewWorkTypeName("");
  };

  const addCompensationType = () => {
    if (!newCompensationTypeName.trim()) return;

    const newCompensationType = {
      name: newCompensationTypeName,
      active: true,
    };

    const createNewCompensationType = async () => {
      const responseData = await createCompensationType(newCompensationType);
      fetchCompensationTypes();
    };
    createNewCompensationType();

    setNewCompensationTypeName("");
  };

  const updateWorkTypeFunction = () => {
    if (!editingWorkType) return;

    const updateWorkTypeResponse = async () => {
      const responseData = await updateWorkType(
        editingWorkType._id,
        editingWorkType
      );
      fetchWorkTypes();
    };

    updateWorkTypeResponse();
    setEditingWorkType(null);
  };

  const updateCompensationTypeFunction = () => {
    if (!editingCompensationType) return;

    const updateCompensationTypeResponse = async () => {
      const responseData = await updateCompensationType(
        editingCompensationType._id,
        editingCompensationType
      );
      fetchCompensationTypes();
    };

    updateCompensationTypeResponse();
    setEditingCompensationType(null);
  };

  const toggleWorkTypeStatusFunction = (id) => {

    const toggleWorkTypeStatusResponse = async () => {
      const responseData = await toggleWorkTypeStatus(id);
      fetchWorkTypes();  
    }

    toggleWorkTypeStatusResponse()
  };

  const toggleCompensationTypeStatusFunction = (id) => {
    const toggleCompensationTypeStatusResponse = async () => {
      const responseData = await toggleCompensationTypeStatus(id);
      fetchCompensationTypes();  
    }
    toggleCompensationTypeStatusResponse()
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  return (
    <div style={{ marginTop: "20px" }}>
      <Card>
        <CardHeader
          title={
            <Typography
              variant="h5"
              style={{ display: "flex", alignItems: "center", gap: "8px" }}
            >
              <FaCog style={{ color: "#1976d2" }} />
              Admin Management
            </Typography>
          }
        />
        <CardContent>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="Work Types" value="workTypes" />
            <Tab label="Compensation Types" value="compensationTypes" />
          </Tabs>

          {tabValue === "workTypes" && (
            <div style={{ marginTop: "20px" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-end",
                  gap: "16px",
                  marginBottom: "20px",
                }}
              >
                <TextField
                  label="Add New Work Type"
                  variant="standard"
                  fullWidth
                  value={newWorkTypeName}
                  onChange={(e) => setNewWorkTypeName(e.target.value)}
                />
                <Button
                  variant="contained"
                  onClick={addWorkType}
                  startIcon={<FaPlus />}
                >
                  Add
                </Button>
              </div>

              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow style={{ backgroundColor: "#f5f5f5" }}>
                      <TableCell>Name</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {workTypes?.map((type) => (
                      <TableRow key={type._id} hover>
                        <TableCell>{type.name}</TableCell>
                        <TableCell>
                          <Badge color={type.active ? "primary" : "secondary"}>
                            {type.active ? "Active" : "Inactive"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div style={{ display: "flex", gap: "8px" }}>
                            <Button
                              variant="outlined"
                              startIcon={<FaEdit />}
                              onClick={() => setEditingWorkType(type)}
                            >
                              Edit
                            </Button>
                            <Switch
                              checked={type.active}
                              onChange={() => toggleWorkTypeStatusFunction(type._id)}
                              color="primary"
                            />
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </div>
          )}

          {tabValue === "compensationTypes" && (
            <div style={{ marginTop: "20px" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-end",
                  gap: "16px",
                  marginBottom: "20px",
                }}
              >
                <TextField
                  label="Add New Compensation Type"
                  variant="standard"
                  fullWidth
                  value={newCompensationTypeName}
                  onChange={(e) => setNewCompensationTypeName(e.target.value)}
                />
                <Button
                  variant="contained"
                  onClick={addCompensationType}
                  startIcon={<FaPlus />}
                >
                  Add
                </Button>
              </div>

              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow style={{ backgroundColor: "#f5f5f5" }}>
                      <TableCell>Name</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {compensationTypes?.map((type) => (
                      <TableRow key={type._id} hover>
                        <TableCell>{type.name}</TableCell>
                        <TableCell>
                          <Badge color={type.active ? "primary" : "secondary"}>
                            {type.active ? "Active" : "Inactive"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div style={{ display: "flex", gap: "8px" }}>
                            <Button
                              variant="outlined"
                              startIcon={<FaEdit />}
                              onClick={() => setEditingCompensationType(type)}
                            >
                              Edit
                            </Button>
                            <Switch
                              checked={type.active}
                              onChange={() =>
                                toggleCompensationTypeStatusFunction(type._id)
                              }
                              color="primary"
                            />
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Work Type Dialog */}
      <Dialog
        open={Boolean(editingWorkType)}
        onClose={() => setEditingWorkType(null)}
      >
        <DialogTitle>Edit Work Type</DialogTitle>
        <DialogContent>
          <TextField
            label="Name"
            fullWidth
            value={editingWorkType ? editingWorkType.name : ""}
            onChange={(e) =>
              setEditingWorkType({ ...editingWorkType, name: e.target.value })
            }
            style={{ marginTop: "16px" }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingWorkType(null)}>Cancel</Button>
          <Button onClick={updateWorkTypeFunction} color="primary">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Compensation Type Dialog */}
      <Dialog
        open={Boolean(editingCompensationType)}
        onClose={() => setEditingCompensationType(null)}
      >
        <DialogTitle>Edit Compensation Type</DialogTitle>
        <DialogContent>
          <TextField
            label="Name"
            fullWidth
            value={editingCompensationType ? editingCompensationType.name : ""}
            onChange={(e) =>
              setEditingCompensationType({
                ...editingCompensationType,
                name: e.target.value,
              })
            }
            style={{ marginTop: "16px" }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingCompensationType(null)}>
            Cancel
          </Button>
          <Button onClick={updateCompensationTypeFunction} color="primary">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
